import React, { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import toast from 'react-hot-toast'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  Button,
  Indicators,
  RadioGroup,
  RadioGroupItem,
  Label,
} from 'ui'
import { AccessLevel, AdminUser } from '../../../queries/types'
import { useCreateAdminUser, useUpdateAdminUser } from '../../../queries'
import { useUpdateProfileMutation } from '../../../queries/useUpdateProfileMutation'

const getUserFormSchema = (isEditing: boolean) => {
  const baseSchema = yup.object({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    emailAddress: yup
      .string()
      .required('Email is required')
      .email('Invalid email address'),
  })

  if (isEditing) {
    return baseSchema
  }

  return baseSchema.shape({
    role: yup.string().required('Role is required'),
  })
}

export type UserFormValues = {
  firstName: string
  lastName: string
  emailAddress: string
  role?: string
}

export type UserFormSuccessFn = (userId: string) => void

interface IProps {
  loading: boolean
  user?: AdminUser
  onSuccess?: UserFormSuccessFn
}

export const UserForm: React.FC<IProps> = ({ loading, user, onSuccess }) => {
  const [createUser] = useCreateAdminUser()
  const [updateUser] = useUpdateAdminUser()
  const { mutateAsync: updateProfile } = useUpdateProfileMutation()

  const isEditing = !!user?.id
  const userFormSchema = getUserFormSchema(isEditing)

  const form = useForm<UserFormValues>({
    mode: 'onBlur',
    resolver: yupResolver(userFormSchema),
    defaultValues: {
      // Use profile names if available, fall back to user names
      firstName: user?.profile?.firstName || user?.firstName || '',
      lastName: user?.profile?.lastName || user?.lastName || '',
      emailAddress: user?.emailAddress || '',
      ...(isEditing ? {} : { role: 'EDUCATOR' }),
    },
  })

  const handleSubmitForm = useCallback(
    async (values: UserFormValues) => {
      try {
        if (user?.id) {
          // For updates, use the profile update mutation
          await updateProfile({
            userId: user.id,
            firstName: values.firstName,
            lastName: values.lastName,
          } as any)

          toast.success('User updated successfully')
          onSuccess?.(user.id)
        } else {
          // For creates, still use the create admin user endpoint
          const result = await createUser({ variables: values })
          const response = result.response

          if (response.errors && response.errors.length > 0) {
            response.errors.forEach((errorMessage) => {
              toast.error(errorMessage)
            })
            return
          }

          if (response.user) {
            toast.success('User created successfully')
            onSuccess?.(response.user.id)
          }
        }
      } catch (error) {
        console.error('Error submitting form:', error)
        toast.error(
          error instanceof Error ? error.message : 'An error occurred'
        )
      }
    },
    [user, createUser, updateProfile, onSuccess]
  )

  const isSubmitting = form.formState.isSubmitting
  const disabled = isSubmitting || loading

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmitForm)}
        className="space-y-6"
      >
        {/* First Name and Last Name Row */}
        <div className="flex flex-col sm:flex-row gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="First Name"
                    disabled={disabled}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Last Name"
                    disabled={disabled}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Email Field */}
        <FormField
          control={form.control}
          name="emailAddress"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  disabled={disabled}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Role Radio Group - Only show when creating new user */}
        {!isEditing && (
          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={disabled}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="EDUCATOR" id="educator" />
                      <Label htmlFor="educator">Educator</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="PROFESSIONAL" id="professional" />
                      <Label htmlFor="professional">Professional</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Submit Button */}
        <Button type="submit" disabled={disabled} className="w-full sm:w-auto">
          {isSubmitting && <Indicators.Spinner size="sm" className="mr-2" />}
          {user ? 'Update User' : 'Create User'}
        </Button>
      </form>
    </Form>
  )
}
